import { useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { useAdmin } from '@/hooks/useAdmin';
import { supabase } from '@/integrations/supabase/client';
import { useIsMobile } from '@/hooks/use-mobile';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import {
  LayoutDashboard, BarChart3, FileText, Rss, Mail, Users,
  Inbox, Settings, LogOut, Newspaper, MessageCircle, Contact, MessageSquare, Menu, FileBarChart, Globe,
  ClipboardList, FolderKanban, Headphones
} from 'lucide-react';

const navItems = [
  { to: '/admin', icon: LayoutDashboard, label: 'Dashboard', end: true },
  { to: '/admin/analytics', icon: BarChart3, label: 'Analytics' },
  { to: '/admin/blog', icon: FileText, label: 'Blog Manager', end: true },
  { to: '/admin/blog/new', icon: Newspaper, label: 'AI Blog Editor' },
  { to: '/admin/comments', icon: MessageCircle, label: 'Comments' },
  { to: '/admin/rss', icon: Rss, label: 'RSS Scraper' },
  { to: '/admin/newsletter', icon: Mail, label: 'Newsletter' },
  { to: '/admin/subscribers', icon: Users, label: 'Subscribers' },
  { to: '/admin/contacts', icon: Contact, label: 'Contacts' },
  { to: '/admin/cx-requests', icon: Headphones, label: 'CX Requests' },
  { to: '/admin/conversations', icon: MessageSquare, label: 'Conversations' },
  { to: '/admin/reports', icon: FileBarChart, label: 'Report Generator' },
  { to: '/admin/geo', icon: Globe, label: 'GEO Tools' },
  { to: '/admin/checklists', icon: ClipboardList, label: 'Checklists' },
  { to: '/admin/projects', icon: FolderKanban, label: 'Projects' },
  { to: '/admin/inbox', icon: Inbox, label: 'Inbox' },
  { to: '/admin/settings', icon: Settings, label: 'Settings' },
];

const NavItems = ({ onNavigate }: { onNavigate?: () => void }) => (
  <>
    {navItems.map(({ to, icon: Icon, label, end }) => (
      <NavLink
        key={to}
        to={to}
        end={end}
        onClick={onNavigate}
        className={({ isActive }) =>
          `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
            isActive ? 'text-white' : 'hover:bg-[var(--blue-light)]'
          }`
        }
        style={({ isActive }) => ({
          background: isActive ? 'var(--blue)' : undefined,
          color: isActive ? 'var(--white)' : 'var(--slate)',
        })}
      >
        <Icon className="w-4 h-4" />
        {label}
      </NavLink>
    ))}
  </>
);

const AdminLayout = () => {
  const { loading, isAdmin } = useAdmin();
  const isMobile = useIsMobile();
  const [sheetOpen, setSheetOpen] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--off-white)' }}>
        <div className="animate-pulse text-muted-foreground text-lg">Loading…</div>
      </div>
    );
  }

  if (!isAdmin) return null;

  const handleLogout = async () => {
    await supabase.auth.signOut();
  };

  if (isMobile) {
    return (
      <div className="min-h-screen flex flex-col" style={{ background: 'var(--off-white)' }}>
        {/* Sticky top bar */}
        <header className="sticky top-0 z-50 flex items-center justify-between h-14 px-4" style={{ background: 'var(--white)', borderBottom: '1px solid var(--line)' }}>
          <h1 className="text-lg font-fraunces font-bold tracking-tight" style={{ color: 'var(--blue)' }}>
            ADDIS<span style={{ color: 'var(--amber)' }}>.</span>Admin
          </h1>
          <button onClick={() => setSheetOpen(true)} className="p-2 rounded-md" style={{ color: 'var(--ink)' }} aria-label="Open menu">
            <Menu size={22} />
          </button>
        </header>

        {/* Sheet drawer */}
        <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
          <SheetContent side="left" className="w-[280px] p-0 flex flex-col">
            <SheetTitle className="sr-only">Admin Navigation</SheetTitle>
            <div className="p-6" style={{ borderBottom: '1px solid var(--line)' }}>
              <h1 className="text-lg font-fraunces font-bold tracking-tight" style={{ color: 'var(--blue)' }}>
                ADDIS<span style={{ color: 'var(--amber)' }}>.</span>Admin
              </h1>
            </div>
            <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
              <NavItems onNavigate={() => setSheetOpen(false)} />
            </nav>
            <div className="p-3" style={{ borderTop: '1px solid var(--line)' }}>
              <button
                onClick={() => { handleLogout(); setSheetOpen(false); }}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors w-full hover:bg-red-50"
                style={{ color: 'var(--slate)' }}
              >
                <LogOut className="w-4 h-4" />
                Sign Out
              </button>
            </div>
          </SheetContent>
        </Sheet>

        {/* Content */}
        <main className="flex-1 overflow-auto">
          <div className="p-4">
            <Outlet />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex" style={{ background: 'var(--off-white)' }}>
      {/* Desktop Sidebar */}
      <aside className="w-64 flex flex-col" style={{ background: 'var(--white)', borderRight: '1px solid var(--line)' }}>
        <div className="p-6" style={{ borderBottom: '1px solid var(--line)' }}>
          <h1 className="text-lg font-fraunces font-bold tracking-tight" style={{ color: 'var(--blue)' }}>
            ADDIS<span style={{ color: 'var(--amber)' }}>.</span>Admin
          </h1>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          <NavItems />
        </nav>
        <div className="p-3" style={{ borderTop: '1px solid var(--line)' }}>
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors w-full hover:bg-red-50"
            style={{ color: 'var(--slate)' }}
          >
            <LogOut className="w-4 h-4" />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto">
        <div className="p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
